// Modal plumbing shared by every dialog on the site (the wall viewer, the auth dialog, the shell's
// BottomSheet, the Cash out dialogs). Its own module, so the dialogs themselves can load on demand.
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"])';

type CloseWatcherLike = {
  oncancel: ((e: Event) => void) | null;
  onclose: (() => void) | null;
  destroy(): void;
};

/** Open dialogs, oldest first. Only the newest one handles keys and keeps focus. */
const stack: object[] = [];
let lockedOverflow = '';

/**
 * Modal plumbing for the site's dialogs: locks page scroll, moves focus inside (to `initial`, or
 * the panel), keeps Tab inside, closes on Escape and on Android's back button (a CloseWatcher, so
 * back closes the dialog instead of leaving the page), and hands focus back to whatever opened it.
 * Dialogs stack: a sheet opened over another one owns the keys and focus until it closes.
 * Used by WallModal, the auth dialog, the shell's BottomSheet and the Cash out dialogs.
 *
 * `locked` (a BottomSheet with a request in flight): close requests still reach onClose, which
 * gives feedback instead of closing, and back keeps being caught by this dialog instead of the
 * second press leaving the page.
 */
export function useModal(
  panel: RefObject<HTMLElement | null>,
  onClose: () => void,
  initial?: RefObject<HTMLElement | null>,
  { locked = false }: { locked?: boolean } = {},
) {
  const close = useRef(onClose);
  const held = useRef(locked);
  useLayoutEffect(() => {
    close.current = onClose;
    held.current = locked;
  });
  useEffect(() => {
    const me = {};
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!stack.length) {
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    stack.push(me);
    const top = () => stack[stack.length - 1] === me;
    const el = panel.current;
    (initial?.current ?? el)?.focus({ preventScroll: true });

    // Android back (and other close requests): the newest watcher gets it, so back closes the top
    // dialog, one per press. Browsers without CloseWatcher keep Escape and the dialog's own
    // controls. While locked, the request is cancelled when the browser allows it (it does once
    // per user interaction) and the watcher stays; when it doesn't, the watcher is spent, so a
    // fresh one takes its place for the next press.
    let watcher: CloseWatcherLike | null = null;
    const CW = (window as unknown as { CloseWatcher?: new () => CloseWatcherLike }).CloseWatcher;
    const watch = () => {
      if (!CW) return;
      try {
        const w = new CW();
        w.oncancel = (e) => {
          // Not cancelable: the close event follows, and onclose handles it.
          if (!held.current || !e.cancelable) return;
          e.preventDefault();
          close.current();
        };
        w.onclose = () => {
          close.current();
          if (held.current && watcher === w && top()) watch();
        };
        watcher = w;
      } catch {
        watcher = null;
      }
    };
    watch();

    const items = () =>
      el
        ? [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
            (n) => n.getClientRects().length > 0,
          )
        : [];
    const onKey = (e: KeyboardEvent) => {
      if (!top()) return;
      if (e.key === 'Escape') {
        // Cancelled so the browser doesn't also turn this key into a close request (twice closed).
        e.preventDefault();
        return close.current();
      }
      if (e.key !== 'Tab' || !el) return;
      const list = items();
      const first = list[0];
      const last = list[list.length - 1];
      if (!first || !last) return e.preventDefault();
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // Focus that escapes the panel (e.g. tabbing out of a partner iframe) comes back to it.
    const onFocusIn = (e: FocusEvent) => {
      if (top() && el && e.target instanceof Node && !el.contains(e.target))
        (items()[0] ?? el).focus();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      watcher?.destroy();
      stack.splice(stack.indexOf(me), 1);
      if (!stack.length) document.body.style.overflow = lockedOverflow;
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [panel, initial]);
}
